// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import Select2, { DataSelect2 } from 'wdna-select2';

import { UserDO } from 'wdna-components/lib/types.new';
import { RowDataUpdatedEvent } from 'wdna-grid-table/lib/types';
import Table from 'wdna-components/lib/table/Table';
import { messageError, messageWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { company, CompanyDO } from '../../management/requests';

type ReportDO = {
  id: number;
  name: string;
}

type Props = unknown;

interface State {
  loading: boolean;
  update: string;
  showGrid: boolean;
  companyId?: number;
  companyArray: CompanyDO[];

  data: ReportDO[];
}

export default class Reports extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private userIsURW: boolean;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.userIsURW = false;

    this.state = {
      loading: false,
      update: window.getStr(),
      showGrid: false,
      companyArray: [],
      data: [],
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.userIsURW = hasLevel('role', ['ADM', 'URW']);

    this.load().catch(() => { /* pass */ });
  }

  public shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = async (): Promise<void> => {
    const companyFilter: Record<string, any> = {};
    if (!this.userIsURW) {
      const user: UserDO = _.get(window.store.getState(), ['auth', 'user']);
      companyFilter.id = _.defaultTo(_.toNumber(user.access?.companyId), undefined);
      companyFilter.clientId = _.defaultTo(_.toNumber(user.access?.clientId), undefined);
    }

    this.loadingStart();
    try {
      const companyArray: CompanyDO[] = await company<CompanyDO[]>('list', undefined, companyFilter) || [];
      this.setLocalState({
        companyArray,
        companyId: companyFilter?.id
      });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: false,
    });
  };

  public onGo = (): void => {
    const { companyId } = this.state;
    if (companyId !== undefined) {
      this.loadingStart();
      company<string[]>('reports', String(companyId))
        ?.then((resp: string[]) => {
          const data: ReportDO[] = resp.map((item: string, index: number): ReportDO => { return { id: index, name: item, }; });
          this.setLocalState({ data });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    } else {
      messageWarning('Warning', 'You must select a company to get the report list');
    }
  };

  public onInfo = (value: ReportDO): void => {
    const { companyId } = this.state;
    const req: Record<string, any> | null = { file: value.name };

    this.loadingStart();
    company('report', String(companyId), req)
      ?.then(() => {
        /* pass */
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public rowDataUpdated = (_event: RowDataUpdatedEvent): void => {
    this.setLocalState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
    const { ctype } = dataset || {};
    // Process new value from ctype
    let newValue: any = (type === 'checkbox') ? !!checked : value;
    if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
    // Create new data state structure
    const levels: string[] = name.split('.');
    const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
    _.set(nextState, levels, newValue);
    nextState.update = window.getStr();
    this.setLocalState(nextState);
  };

  public render(): JSX.Element {
    const { loading, data, showGrid, companyArray, companyId } = this.state;

    const columnAlias: Record<string, string> = {
      id: 'Id',
      name: 'File Name',
    };
    const columnsToHide: string[] = [];
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'right' },
      name: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };
    const customColumnsDefs: Record<string, any> = {
      id: { suppressSizeToFit: false, minWidth: 50, maxWidth: 50 },
    };

    return (
      <div id="content" className="content">
        <div className="row">
          <div className="col-3 wdna-padding">
            &nbsp;&nbsp;&nbsp;
            <Select2
              name="companyId"
              value={companyId}
              onChange={this.handleChange}
              data={companyArray?.map((item: CompanyDO): DataSelect2 => {
                return { text: item.name, id: item.id };
              })}
              options={{ placeholder: 'Select company' }}
              style={{ width: '150px' }}
            />
          </div>
          <div className="col-6 wdna-padding">
            <button type="button" className="filter-btn btn btn-default" onClick={this.onGo}>
              <i className="wdna-ico-icon-playsearch" />
              <span>GO</span>
            </button>
          </div>
        </div>
        <div style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
          <Table
            data={data || []}
            alias={columnAlias}
            hiddenCols={columnsToHide}
            customCellStyles={columnStyles}
            customColumnsDefs={customColumnsDefs}
            buttons={{ info: true, export: false, filters: false, reset: false }}
            onInfo={this.onInfo}
            loading={loading}
            rowDataUpdated={this.rowDataUpdated}
            pagination={false}
            showRowNumber
          />
        </div>
      </div>
    );
  }
}
